/**
 * coursemenu.jsx — the course catalogue. The menu a university has, before any ladder.
 *
 * FOUNDER, 2026-08-18: "a university will have many courses, ultimately the course menu is separate
 * and then other ladders, adjacent courses can be added and designed."
 *
 * WHAT THIS REPLACES. One flat row of seven equal buttons, in which 2M sat beside CLAUDECODE101 as
 * though they were the same kind of thing. They are not: 2M → CM → MASTER is one profession's
 * certificate progression, CODE101 → CODEAI101 → CLAUDECODE101 is a taught sequence, and
 * NAVALARCH101 stands alone. The row flattened a structure the registry already held.
 *
 * IT RENDERS WHAT THE SERVER COMPILED AND DECIDES NOTHING. No course names, no grouping, no
 * ordering and no fee logic live here — /api/catalogue compiles all of it from courses.json and the
 * ladders themselves. This file's whole job is layout. That is deliberate: seven hand-kept maps in
 * this estate have each, in turn, made a real course invisible, and a menu is exactly where an
 * eighth would grow.
 *
 * TWO CLASSES OF EDGE, NEVER MERGED. A REQUIRES edge is a sequence; a GOOD-TO-KNOW edge is
 * background and gates nothing. NavalArch101 declared itself a prerequisite for the CoC ladder
 * until the founder corrected it — "navalarch is not pre requisite for coc ladder but good to know.
 * imagine a deck cadet who desnt know steel" — one day before this component would have drawn it as
 * an arrow. An arrow on a catalogue is read as a locked door, so the two are styled apart and the
 * soft one says out loud that it gates nothing.
 */
import React from 'react';

const feeWord = {
  seva: { label: 'free — seva', tone: 'cat-fee-seva' },
  paid: { label: 'paid', tone: 'cat-fee-paid' },
  'not ruled': { label: 'no fee decision', tone: 'cat-fee-unruled' },
};

/* ── MONEY IS FORMATTED BY THE PLATFORM, NOT BY A SYMBOL TABLE HERE ────────────────────────────
   A hardcoded { INR: '₹' } map is a second record of something the runtime already knows, and it
   is wrong the first time a course is priced in a currency nobody added to it. Intl is asked, and
   an unknown code falls back to "CODE 5000" — which is ugly and correct, rather than tidy and
   silently mis-stated. */
function money(amount, currency) {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency', currency, maximumFractionDigits: 0,
    }).format(amount);
  } catch { return `${currency} ${amount}`; }
}

/* ── A PRICE IS NEVER SHOWN WITHOUT ITS PERIOD, ITS TAX OR ITS STANDING ────────────────────────
   Three things travel with the number or it is not a price:
     · the PERIOD — "5,000" and "5,000 a month" are different offers
     · the TAX — the server says whether the amount includes it, and the payable figure is COMPUTED
       there (5000 x 1.18), never recomputed here, because two implementations of one sum is the
       fault this project keeps removing
     · PROVISIONAL — the founder marked this rate provisional in the ruling itself, and a surface
       that quietly drops that turns a working rate into a published tariff. It is rendered as a
       word a learner understands, not a flag they have to interpret.
   The panel renders nothing at all when no price is declared — four of the seven courses are ruled
   PAID with no price, and "paid" alone is the honest thing to show for them. */
function Price({ price }) {
  if (!price) return null;
  const inc = price.tax && price.tax.included_in_amount;
  return (
    <div className={`cat-price ${price.provisional ? 'cat-price-prov' : ''}`}>
      <span className="cat-price-amt">{money(price.amount, price.currency)}</span>
      {/* THREE SHAPES OF PERIOD, THREE WORDINGS, BECAUSE THEY ARE THREE DIFFERENT OFFERS.
          "/ month" is a RATE and reads as one. "one-time" is a purchase. A TERM — "6 months" — is
          neither: rendering it "₹30,000 / 6 months" reads as thirty thousand rupees every six
          months, which is not what was ruled and is the opposite of the reassurance a term is meant
          to give. A duration takes "for". The period is still ALWAYS shown; a price without one is
          the thing this panel exists to prevent. */}
      <span className="cat-price-per">
        {price.period === 'one-time' ? ' one-time'
          : /^\d/.test(String(price.period)) ? ` for ${price.period}`
            : ` / ${price.period}`}
      </span>
      {price.tax ? (
        <span className="cat-price-tax">
          {inc ? ` incl. ${price.tax.name}` : ` + ${price.tax.rate_percent}% ${price.tax.name}`}
          {!inc && price.amount_payable != null
            ? ` — ${money(price.amount_payable, price.currency)} payable`
            : null}
        </span>
      ) : null}
      {/* The per-month equivalent for a term, so a reader comparing this against a ladder sold by
          the month is not asked to divide. Explicitly NOT called a saving: no bundle discount has
          been ruled, and a term priced at exactly the monthly rate that implied one would be a
          commercial fact invented. */}
      {price.equivalent_per_month ? (
        <div className="cat-price-equiv">
          the same as {money(price.equivalent_per_month, price.currency)} a month, bought as one term
        </div>
      ) : null}
      {price.provisional ? (
        <div className="cat-price-note">
          Provisional — a working rate, not a published tariff. It may change before you are billed.
        </div>
      ) : null}
    </div>
  );
}

/* ── WHAT THE FEE DOES NOT BUY ─────────────────────────────────────────────────────────────────
   Founder, 2026-08-19: "access to ankr vm is not provided and byok for ai code especially
   claudecode, GLM is only later."
   It sits on the CARD, beside the price, because that is where the decision is made. A requirement
   discovered in chapter 1 after paying is a requirement that was hidden, however plainly it is
   written there.
   Every line is read from the ladder's own declaration and none is a house sentence: CODEAI101 has
   no shell commands at all and telling its reader to bring an API key would be inventing a
   requirement — the same offence as concealing one. The gate measures the declaration against the
   chapters, so these lines cannot drift from what the course actually asks. */
function Provides({ p }) {
  if (!p) return null;
  return (
    <div className="cat-provides">
      <div className="cat-provides-h">What the fee does not include</div>
      {p.ankr_infrastructure === false ? (
        <div className="cat-provides-line">No ANKR machine, account or proxy. You work on hardware you already control.</div>
      ) : null}
      {p.needs_own_api_key ? (
        <div className="cat-provides-line">
          <strong>Bring your own key.</strong> The tool bills to your own Anthropic account. The fee buys the course, not tokens.
        </div>
      ) : (
        <div className="cat-provides-line">No API key needed — nothing here asks you to hold one.</div>
      )}
      {p.needs_to_install
        ? <div className="cat-provides-line">You install the tool yourself.</div>
        : <div className="cat-provides-line">Nothing to install.</div>}
      {/* The GLM line is a WARNING, not a feature, and is worded as one: a reader who buys expecting
          the cheap path from day one has been misled by an omission. */}
      {p.alternatives_note ? (
        <div className="cat-provides-line cat-provides-warn">
          The cheaper model (GLM) is taught in <strong>chapter 21 of 21</strong>, not at the start.
        </div>
      ) : null}
    </div>
  );
}

/* ── ONE COURSE OPEN, THE REST STILL REACHABLE ─────────────────────────────────────────────────
   FOUNDER, 2026-08-19: "i am seeing course menu on url study is like a scroll, it shall be say i
   pick second mates then only second mates course opens and others get collapsed or hidden."
   Seven fully-drawn cards became a page you scroll past to reach the ladder, and each thing added
   today — the price, the tax line, the statutory rule, the disclosure — made it longer. A menu that
   costs a scroll to get past is a menu that gets scrolled past.
   COLLAPSED, NOT HIDDEN, and the distinction is the whole design. Hiding the other six would leave
   a learner unable to see what else exists or switch without hunting for a control; the catalogue's
   job is to show a university, and one course with the rest erased is not one. A closed row still
   names the course, its certificate and what it costs — enough to choose from, in one line. */
function CourseRow({ c, onPick }) {
  const fee = feeWord[c.fee] || feeWord['not ruled'];
  const p = c.price;
  return (
    <button type="button" onClick={() => onPick(c.level)} className="cat-row" aria-expanded="false">
      <span className="cat-row-level">{c.level}</span>
      <span className="cat-row-cert">{c.cert}</span>
      {/* The cost travels with the closed row. A learner comparing courses is comparing prices, and
          making them open each one to see it is the sort of friction that reads as concealment. */}
      <span className="cat-row-cost">
        {/* Worded exactly as the open card words it. A row reading "₹30,000 / 6 months" and a card
            reading "₹30,000 for 6 months" are the same offer described two ways on one screen, and
            the slash is the one that misreads as a recurring charge. */}
        {p ? `${money(p.amount, p.currency)}${p.period === 'one-time' ? '' : /^\d/.test(String(p.period)) ? ` for ${p.period}` : `/${p.period}`}` : fee.label}
      </span>
    </button>
  );
}

function CourseCard({ c, selected, onPick }) {
  const fee = feeWord[c.fee] || feeWord['not ruled'];
  const free = c.free_chapters || [];
  return (
    <button
      type="button"
      onClick={() => onPick(c.level)}
      className={`cat-card ${selected ? 'cat-card-on' : ''}`}
      aria-pressed={selected}
    >
      <div className="cat-card-head">
        <span className="cat-level">{c.level}</span>
        <span className={`cat-fee ${fee.tone}`}>{fee.label}</span>
      </div>

      <div className="cat-cert">{c.cert}</div>

      <Price price={c.price} />
      {/* ── A COURSE THAT CANNOT BE EXAMINED SAYS SO, BEFORE THE PRICE IS PAID ─────────────────
          DP101 is complete, priced and served with NOT ONE of its 24 criteria signed, because DP
          has its own certification scheme and no warranted attestor is in the register. Everything
          else on this card — eight chapters, a price — reads like a course you can be marked on.
          The one fact that contradicts that impression is the one a learner would otherwise find
          out after paying, so it sits directly under the price rather than in a footnote. */}
      {c.criteria && c.criteria.total > 0 && c.criteria.attested === 0 ? (
        <div className="cat-unexaminable">
          <strong>Readable now, not yet examinable.</strong> Its criteria are drafted but none is
          signed, so no chapter can be passed and no record of passing it can exist yet.
        </div>
      ) : null}
      <Provides p={c.provides} />

      <div className="cat-counts">
        {c.chapters} chapter{c.chapters === 1 ? '' : 's'}
        {c.planned ? <span className="cat-planned"> · {c.planned} planned</span> : null}
        {/* A free-chapter list is shown as a COUNT plus the numbers, because "1–5 free" and "5 of
            21 free" answer different questions and a learner deciding where to start asks both. */}
        {free.length ? <span className="cat-free"> · {free.length} free ({free.join(', ')})</span> : null}
      </div>

      {c.requires ? (
        <div className="cat-edge cat-edge-hard">
          Comes after <strong>{c.requires.level}</strong>
          {c.requires.enforced ? '' : <span className="cat-soft-note"> · declared, not enforced</span>}
        </div>
      ) : null}

      {c.required_by?.length ? (
        <div className="cat-edge cat-edge-hard">Leads to <strong>{c.required_by.join(', ')}</strong></div>
      ) : null}

      {/* ── THE THIRD EDGE: THE LAW, WHICH IS NOT A DOOR ON THIS SITE ──────────────────────────
          The founder's first reaction to this catalogue was that the link looked odd: CODEAI101
          declared "comes after CODE101" while 2M, CM and MASTER — a legally ordered progression —
          showed nothing between them. The chain was declared all along, in the ladders' own prose
          and in rules 21 and 22 of the MS (STCW) Rules 2014; nothing drew it.
          It is drawn in its OWN shape, never as `requires`. A "comes after" on a catalogue reads as
          a locked door, and this door is not ours to lock: the certificate chain binds who may sit
          the EXAM, and a serving Chief Mate may buy the Master ladder tomorrow without ever having
          taken ours. So the rule is cited on the card — a learner can go and read it — and the line
          says in plain words that it does not gate enrolment here.
          The ROOT is rendered too. "Nothing is required before this one" is a quoted finding from
          rule 20, and it is the answer to the question a candidate actually arrives with. */}
      {c.certificate_prerequisite ? (
        <div className="cat-edge cat-edge-law">
          {c.certificate_prerequisite.level ? (
            <>Certificate: needs <strong>{c.certificate_prerequisite.certificate
              || c.certificate_prerequisite.level}</strong> first</>
          ) : (
            <>Certificate: <strong>no prior certificate required</strong></>
          )}
          <span className="cat-soft-note">
            {' · '}{c.certificate_prerequisite.rule}
            {c.certificate_prerequisite.gates_enrolment ? '' : ' · does not gate enrolment here'}
          </span>
        </div>
      ) : null}

      {c.good_to_know_for ? (
        <div className="cat-edge cat-edge-soft">
          Good to know for <strong>{c.good_to_know_for.levels.join(', ')}</strong>
          <span className="cat-soft-note"> · background, gates nothing</span>
        </div>
      ) : null}

      {c.credits ? (
        <div className="cat-credit">
          {/* Designed and written are shown separately because they are separately true — the
              course author's credit is the design, and who typed the prose is a smaller fact. */}
          <span>Designed by <strong>{c.credits.designed_by}</strong></span>
          {c.credits.written_by && c.credits.written_by !== c.credits.designed_by
            ? <span className="cat-credit-2"> · written by {c.credits.written_by}</span> : null}
          {c.credits.contributions?.length
            ? <span className="cat-credit-2"> · with {c.credits.contributions.map((x) => x.by).join(', ')}</span> : null}
        </div>
      ) : null}

      {c.name_provisional ? <div className="cat-prov">name provisional</div> : null}
    </button>
  );
}

export default function CourseMenu({ catalogue, level, setLevel }) {
  const cat = catalogue || null;
  if (!cat || !cat.domains?.length) {
    /* An empty catalogue is reported, never rendered as an empty page. If the compiler could not
       read the registry the learner should be told, not shown a university with no courses. */
    return (
      <div className="g-glass shell-panel">
        <div className="g-label">The catalogue</div>
        <p className="g-body">The course catalogue could not be compiled just now. Nothing is missing from your enrolment — this is the menu, not the courses.</p>
      </div>
    );
  }

  const n = cat.counts || {};
  return (
    <div className="g-glass shell-panel">
      <div className="g-label">The catalogue</div>
      <h2 className="g-h2" style={{ margin: 'var(--g-2) 0 var(--g-3)' }}>What this university teaches</h2>
      <p className="g-body">
        {n.courses} courses across {n.domains} subjects · {n.chapters} chapters · {n.free_chapters} of them free.
        The one you are in is open below; tap any other to open it.
      </p>

      {cat.domains.map((d) => (
        <section key={d.domain} className="cat-domain">
          <div className="cat-domain-name">{String(d.domain).replace(/-/g, ' ')}</div>
          {/* The grid only wraps the OPEN card. Closed rows stack, because a row is a line and a
              three-column grid of one-line buttons reads as a toolbar rather than a list. */}
          {d.courses.map((c) => (c.level === level
            ? <div key={c.level} className="cat-grid"><CourseCard c={c} selected onPick={setLevel} /></div>
            : <CourseRow key={c.level} c={c} onPick={setLevel} />))}
        </section>
      ))}
    </div>
  );
}
